import Badge from 'atomic/badge';
import Link from 'atomic/link';
import Rating from 'atomic/rating';
import classNames from 'classnames';
import ProductListImpression from 'components/impressions/product-impression/list/product-list-impression';
import { ProductBoxWideProps } from 'modules/shared/components/product/product-box-wide/product-box-wide.props';
import React from 'react';
import AlertDiscountPriceInfo from 'modules/shared/components/alert-discount-price-info';
import ProductBoxPrice from 'modules/shared/components/product/components/product-box-price';
import ProductAttributes from 'modules/product/views/components/product-attributes';
import ProductBoxName from 'modules/shared/components/product/components/product-box-name';
import { prepareOfferDataForDiscount } from 'modules/shared/helpers/prepare-offer-data-for-discount';
import { SeoLinkRelEnum } from 'modules/seo-module/interfaces/seo-link-rel.enum';
import { LinkTarget } from 'atomic/link/link-target.enum';

import './product-box-wide.desktop.scss';

const ProductBoxWideDesktop: React.FC<{
    product: ProductBoxWideProps;
    className?: string;
    headerTag?: 'div' | 'h2' | 'span';
    containerTag?: 'div' | 'a';
    headerRel?: SeoLinkRelEnum.NOFOLLOW;
    mainImageLinkingToShop?: boolean;
}> = ({ product, className, headerTag = 'h2', containerTag = 'a', headerRel, mainImageLinkingToShop }) => {
    const shouldImageRedirectToOffer = product.offers.count >= 1 && mainImageLinkingToShop && product.offers?.items[0]?.url;
    const {
        discount,
        minPrice30Days,
        price,
        offersCount,
        prevPrice,
        showDiscount,
        productHasNoOffers,
        dealerName
    } = prepareOfferDataForDiscount(
        product.minPrice,
        product.prevPrice,
        product.offers.items,
        product.offers.count
    );

    const productLinkHref = offersCount === 1 ? product.offers.items[0]?.url : product.url;
    const productLinkRel = offersCount === 1 ? SeoLinkRelEnum.NOFOLLOW_NOOPENER_NOREFERRER : SeoLinkRelEnum.NOFOLLOW;
    const productLinkTarget = offersCount === 1 ? LinkTarget.BLANK : void 0;

    return (
        <ProductListImpression
            trackedData={{ ...product, category: { name: product.name }, price }}
            className={classNames({
                'product-box-wide-d': true,
                [String(className)]: Boolean(className)
            })}
        >
            {React.createElement(containerTag, {
                'className': 'product-box-wide-d-container',
                'href': containerTag === 'a' ? productLinkHref : void 0,
                'rel': containerTag === 'a' ? productLinkRel : void 0,
                'target': containerTag === 'a' ? productLinkTarget : void 0,
                'aria-label': containerTag === 'a' ? product.name : void 0
            }, (''))}
            <>
                {product.sponsored && (
                    <div className="product-box-wide-d-sponsored">
                        <span>Oferta promowana</span>
                        {product.dsaurl && (
                            <a
                                className="product-box-wide-d-sponsored__info"
                                href={product.dsaurl}
                                target={LinkTarget.BLANK}
                                rel={SeoLinkRelEnum.NOOPENER_NOREFERRER}
                                aria-label="Info"
                            >
                                ⓘ
                            </a>
                        )}
                    </div>
                )}
                {product.photos.main && (
                    <div className="product-box-wide-d-image">
                        { shouldImageRedirectToOffer
                            ? (
                                <>
                                    <a
                                        href={product.offers?.items[0]?.url}
                                        rel={SeoLinkRelEnum.NOFOLLOW}
                                        className="product-box-wide-d-image__link"
                                    >
                                        <img
                                            src={product.photos.main?.thumb}
                                            alt={`${product.category.name} - ${product.name} - miniaturka - grafika 1`} loading="lazy"
                                        />
                                    </a>
                                </>
                            )
                            : (
                                <>
                                    <img
                                        src={product.photos.main?.thumb}
                                        alt={`${product.category.name} - ${product.name} - miniaturka - grafika 1`} loading="lazy"
                                    />
                                </>
                            )
                        }

                        <div className="product-box-wide-d-badges">
                            {showDiscount && (
                                <Badge color="red">
                                    <span>{discount}%</span>
                                </Badge>
                            )}
                        </div>
                    </div>
                )}
                <div className="product-box-wide-d-main">
                    <div className="product-box-wide-d-desc">
                        <a
                            href={product.url}
                            data-ga="go-to-product-page go-to-product-page--d"
                            rel={productHasNoOffers ? SeoLinkRelEnum.NOFOLLOW : headerRel}
                        >
                            {React.createElement(
                                headerTag,
                                { className: 'product-box-wide-d-main__title' },
                                product.name
                            )}
                        </a>
                        <div className="product-box-wide-d-rating">
                            <Rating opinions={product.opinions} />
                        </div>
                        <div className="product-box-wide-d-price">
                            <ProductBoxPrice
                                price={price}
                                prevPrice={prevPrice}
                                offersCount={offersCount}
                                discount={discount}
                            />
                            <ProductBoxName
                                dealerName={dealerName}
                                productHasNoOffers={productHasNoOffers}
                                className="product-box-wide-d-price-producer-name"
                            />
                        </div>
                        {showDiscount && (
                            <AlertDiscountPriceInfo paddingTop={4} minPrice30Days={minPrice30Days} />
                        )}
                        <div className="product-box-wide-d-buttons">
                            <object
                                data="javascript:void(0)"
                                type="text/plain"
                            >
                                {offersCount > 1 && (
                                    <Link
                                        href={product.url}
                                        data-ga="go-to-product-page go-to-product-page--d"
                                        data-cy="go-to-product-page"
                                        size="lg"
                                        color="transparent"
                                        type="button"
                                        rel={SeoLinkRelEnum.NOFOLLOW}
                                    >
                                        Porównaj oferty ({offersCount})
                                    </Link>
                                )}
                                <Link
                                    href={product.offers.items[0]?.url || product.url}
                                    data-ga="go-to-shop go-to-shop--d"
                                    data-cy="go-to-shop"
                                    size="lg"
                                    color={product.offers.items[0]?.url ? 'primary' : 'transparent'}
                                    type="button"
                                    target={LinkTarget.BLANK}
                                    rel={SeoLinkRelEnum.NOFOLLOW_NOOPENER_NOREFERRER}
                                >
                                    {product.offers.items[0]?.url ? 'Idź do sklepu' : 'Zobacz produkt'}
                                </Link>
                            </object>
                        </div>
                    </div>
                    {(product.attributes.length !== 0 || product?.producer?.name) && (
                        <div className="product-box-wide-d-attributes">
                            <ProductAttributes
                                attributes={product.attributes}
                                producer={product?.producer}
                            />
                        </div>
                    )}
                </div>
            </>
        </ProductListImpression>
    );
};

export default ProductBoxWideDesktop;
